/**
 * „Laipsnis" — the pagrindas as a rod, and a press that lays down `a` copies
 * of everything she has.
 *
 * Serves `laipsnis-su-naturaliuoju-rodikliu`, both of its Moves, and is drawn
 * to serve the rest of 7 klasė's chapter 2 without a second hand. The book's
 * own first picture of a power is on p. 26: a side of `a`, a square of side
 * `a`, then a cube of edge `a`. This is that picture carried past the third
 * direction. It opens on `a` itself, a rod of `|a|` cubes, which is the first
 * factor the book writes. Each press takes the whole block and lays `a` of it
 * side by side along the next axis, so the block runs rod, flat, cube, then a
 * rod of cubes, a flat of cubes, a cube of cubes.
 *
 * **The rodiklis is how many factors the block is made of**: the rod it
 * opened on, and one more for every press. That is p. 28's own definition,
 * „n dauginamųjų", and the head under the block writes those factors out as
 * she makes them. It never writes the value.
 *
 * **It opens on the rod and never on one cube.** A single cube is `a⁰`, the
 * empty product, and nothing in 5 · 5 · 5 · 5 is a 1. Opening on it put an
 * unexplained object on the screen that belongs to p. 36. It arrives there,
 * when pressing backwards from the rod leaves one cube.
 *
 * **What she had before the last press is brown**, in the corner of what she
 * has, and the teal beside it is the `a − 1` copies the press laid down. So
 * every screen reads „· 5 is five of what you had", which is aⁿ = a · aⁿ⁻¹.
 *
 * ## Why this and never the strip
 *
 * The first lesson for this topic was built on `Juosta`, and it taught the
 * wrong thing. `Juosta` is one whole cut finer, so a power drawn on it shrinks:
 * 2³ was a strip in eight slivers and 3² beat it by being more finely
 * shredded. **A power grows**, and a drawing of one has to get bigger when the
 * operation is done again. `LAIPSNIAI.md` has the rest of that account.
 *
 * The strip's premise does come back, one chapter later and in its own place:
 * pressing backwards from the unit cuts it, which is what `a⁻ⁿ` is on p. 36.
 * The geometry below is signed for that reason, and nothing in 2.2 reaches it.
 *
 * ## What 3 klasė already knows about this drawing
 *
 * `Ryšulys` draws a thousand as a cube of edge ten, a hundred as a flat, a ten
 * as a rod. That is this drawing with `a = 10`, three presses in, and it is
 * why the first rung of the base-ten block is the same object a child met at
 * nine. Nothing is imported from there: the cube is drawn here in its own
 * geometry because here it has to go on growing.
 *
 * ## The sign is a turn, and it uses the same press
 *
 * A negative pagrindas lays its copies and **turns the block over the floor**:
 * above the floor is plus, hanging under it is minus, and a turned block
 * shows its other colour. `(−4)¹` opens already under the floor, which is
 * true. Each further factor turns it again, so the sign of `(−4)⁹` is whether
 * nine factors end below. That is multiplication by −1 as a half turn, which
 * is what it is.
 *
 * ## What it never does
 *
 * **It never writes the value.** Thirty-two is what `2⁵` asks for, and a
 * drawing that printed it would be the answer with a button on it. The only
 * numerals are the ones her presses wrote in the head.
 *
 * **It never stops at the question's rodiklis.** The press runs to the edge of
 * what the drawing can hold, and a button that died on the fifth press would
 * be telling her that five was the number. The ceiling is the drawing's own
 * and is the same for every question on the same pagrindas.
 */
import { useEffect, useState } from "react";

import { FAINT, FONT, INK, Priemone, Reset } from "./kit";

/* ------------------------------------------------------------------ focus */

/** One power on the question's screen. `rodiklis` is absent wherever it is
 *  the answer: „5 · 5 · 5 · 5 — kuris užrašas?" gives the pagrindas and asks
 *  for the count. */
export interface Laipsnis {
  pagrindas: number;
  rodiklis?: number;
}

/**
 * The powers a question puts in front of her, authored per item in the bank.
 *
 * One for almost everything; two or more where the question compares or
 * orders them, and then they stand side by side at **one scale**, because a
 * comparison drawn at two scales is two pictures that agree with anything.
 */
export interface LaipsnioFokusas {
  kind: "laipsnis";
  laipsniai: Laipsnis[];
}

/** How many powers one screen can hold side by side and still be read. */
const DAUGIAUSIA = 4;

/** The largest pagrindas the drawing takes. Twelve is a rod of twelve, a
 *  flat of a hundred and forty four, and the cube of it is past `KRASTAS`. */
const DIDZIAUSIAS = 12;

/** The longest edge, in units, the block may reach. Past a hundred a unit is
 *  under a pixel and the block is a texture; the grid lines of the bigger
 *  copies still read, but the unit she started from is gone. */
const KRASTAS = 100;

/** Presses one block can take, whatever the pagrindas. */
const DAUGIAUSIA_KARTU = 12;

/**
 * How many presses this pagrindas can take before the block stops being a
 * drawing. The same for every question on it, so the ceiling says nothing
 * about any one question's rodiklis.
 */
export function lubos(pagrindas: number): number {
  const a = Math.abs(pagrindas);
  let n = 0;
  while (n < DAUGIAUSIA_KARTU && Math.pow(a, Math.ceil((n + 1) / 3)) <= KRASTAS) n += 1;
  return n;
}

/**
 * What this drawing can hold, checked rather than cast. The same door for the
 * bank and for a lesson beat, so a focus the drawing cannot hold leaves both
 * empty rather than drawing something false.
 */
export function asLaipsnis(value: unknown): LaipsnioFokusas | undefined {
  if (!value || typeof value !== "object") return undefined;
  const f = value as Record<string, unknown>;
  if (f.kind !== "laipsnis") return undefined;
  if (!Array.isArray(f.laipsniai)) return undefined;
  if (f.laipsniai.length < 1 || f.laipsniai.length > DAUGIAUSIA) return undefined;
  const laipsniai: Laipsnis[] = [];
  for (const raw of f.laipsniai) {
    if (!raw || typeof raw !== "object") return undefined;
    const one = raw as Record<string, unknown>;
    const pagrindas = Number(one.pagrindas);
    if (!Number.isInteger(pagrindas)) return undefined;
    if (Math.abs(pagrindas) < 2 || Math.abs(pagrindas) > DIDZIAUSIAS) return undefined;
    if (one.rodiklis === undefined) {
      laipsniai.push({ pagrindas });
      continue;
    }
    const rodiklis = Number(one.rodiklis);
    if (!Number.isInteger(rodiklis) || rodiklis < 1) return undefined;
    // A question whose power the block cannot reach gets no door. A drawing
    // that ran out one press short of the question would argue it has no
    // answer.
    if (rodiklis > lubos(pagrindas)) return undefined;
    laipsniai.push({ pagrindas, rodiklis });
  }
  return { kind: "laipsnis", laipsniai };
}

/**
 * One rung, on every question, and it is the whole method: pressing is the
 * power. A second picture would have to be the value, and the value is what
 * every question on this topic asks for.
 */
export function laipsnisRungs(_focus: unknown): number {
  return 1;
}

/** A beat's `kartai` read as factors per power, clamped to what each one can
 *  take. Anything missing is a block nobody has pressed, which is one factor:
 *  the pagrindas itself. */
export function kartaiOf(value: unknown, focus: LaipsnioFokusas): number[] {
  const list = Array.isArray(value) ? value : [];
  return focus.laipsniai.map((l, i) => {
    const n = Number(list[i]);
    return Number.isInteger(n) && n >= 1 ? Math.min(n, lubos(l.pagrindas)) : 1;
  });
}

/* ------------------------------------------------------------------ miss */

/**
 * Her wrong answer, where it has a picture: `5 · 4` or `5 + 5 + 5 + 5`.
 *
 * Both are the same misconception, the power read as `a` laid down `n` times
 * rather than everything multiplied by `a`, `n` times. Either one is a flat of
 * `n` rods of `a`, and drawn beside the block at the same scale it is the
 * whole argument: her flat stops growing after one row, and the block does
 * not. It is drawn from the label she pressed and from nothing else, so it
 * cannot know which option was right.
 */
export function spejimasOf(label: string | undefined): { a: number; n: number } | undefined {
  if (!label) return undefined;
  const clean = label.replace(/\s+/g, "");
  const kart = clean.match(/^(\d+)·(\d+)$/);
  if (kart) {
    const a = Number(kart[1]);
    const n = Number(kart[2]);
    return a >= 2 && a <= DIDZIAUSIAS && n >= 2 && n <= DIDZIAUSIAS ? { a, n } : undefined;
  }
  const terms = clean.split("+");
  if (terms.length >= 2 && terms.every((t) => /^\d+$/.test(t) && t === terms[0])) {
    const a = Number(terms[0]);
    return a >= 2 && a <= DIDZIAUSIAS && terms.length <= DIDZIAUSIAS ? { a, n: terms.length } : undefined;
  }
  return undefined;
}

/* --------------------------------------------------------------- geometry */
//
// Isometric, with the unit cube at [0,1]³ in the corner nearest the reader and
// the block growing away from her: x runs right and down, y runs left and
// down, z runs up. So the unit's two front faces are always faces of the
// block, however big it gets, and a press never moves the corner she started
// from.

const C30 = Math.cos(Math.PI / 6);
const S30 = 0.5;

/** Which power of `a` each edge is after `n` presses. The presses go x, y, z,
 *  x, y, z…, so the three never differ by more than one. Signed, because
 *  2.6 presses backwards through the same formula. */
export function laipsniaiKrastiniu(n: number): [number, number, number] {
  return [Math.ceil(n / 3), Math.ceil((n - 1) / 3), Math.floor(n / 3)];
}

interface Blokas {
  /** Edge lengths in units. */
  L: [number, number, number];
  /** Which power of `a` each edge is. */
  E: [number, number, number];
  a: number;
  /** Hanging under the floor: a negative pagrindas turned an odd number of
   *  times. */
  zemyn: boolean;
  /** What she had before the last press, as edge lengths, sitting in the
   *  front corner of what she has now. The whole block on the first factor,
   *  because nothing has been pressed yet and all of it is what she has. */
  P?: [number, number, number];
}

const krastines = (a: number, n: number): [number, number, number] => {
  const E = laipsniaiKrastiniu(n);
  return [Math.pow(a, E[0]), Math.pow(a, E[1]), Math.pow(a, E[2])];
};

/** `n` factors of the pagrindas. One factor is the pagrindas itself: a rod of
 *  `|a|`, which is what the book writes first and what the screen opens on. */
function blokas(pagrindas: number, n: number): Blokas {
  const a = Math.abs(pagrindas);
  const L = krastines(a, n);
  return {
    L,
    E: laipsniaiKrastiniu(n),
    a,
    zemyn: pagrindas < 0 && n % 2 === 1,
    P: n <= 1 ? L : krastines(a, n - 1),
  };
}

/** A flat of `n` rods of `a`, one unit thick: her wrong answer, as a block. */
function plokste(a: number, n: number): Blokas {
  return { L: [a, n, 1], E: [1, 1, 0], a, zemyn: false };
}

/** The block's extent on the page, per unit of scale, measured from the
 *  projection of the origin. The unit cube is always in it, because the unit
 *  is always drawn. */
function ribos(b: Blokas, grindys = false) {
  const R = grindys ? uzribis(b) : 0;
  const Lx = Math.max(b.L[0], 1) + R;
  const Ly = Math.max(b.L[1], 1) + R;
  const zlo = b.zemyn ? -b.L[2] : 0;
  const zhi = b.zemyn ? 0 : b.L[2];
  const xs: number[] = [];
  const ys: number[] = [];
  for (const x of [1 - Lx, 1 + R]) {
    for (const y of [1 - Ly, 1 + R]) {
      for (const z of [zlo, zhi]) {
        xs.push((x - y) * C30);
        ys.push((x + y) * S30 - z);
      }
    }
  }
  return { x0: Math.min(...xs), x1: Math.max(...xs), y0: Math.min(...ys), y1: Math.max(...ys) };
}

interface Kamera {
  s: number;
  cx: number;
  cy: number;
}

const pt = (k: Kamera, x: number, y: number, z: number) =>
  `${(k.cx + (x - y) * C30 * k.s).toFixed(1)},${(k.cy + (x + y) * S30 * k.s - z * k.s).toFixed(1)}`;

/* ------------------------------------------------------------------ view */

/** One block's column, in view units. The drawing is one SVG across every
 *  column so that one scale governs all of them. */
const COL_W = 260;
const VB_H = 250;
const PAD = 16;

const TOP = "#CDEBEA";
const FRONT = "#AEDCDA";
const SIDE = "#8FC6C3";
/** The unit she started from. The brown the drawings already use for what is
 *  hers and quiet, at a weight that survives being eight pixels across. */
const UNIT = "#C9A77C";
const FLOOR = "#EFE7DA";
/** The block's other side, shown when it has been turned under the floor. */
const TOP_N = "#DADDE8";
const FRONT_N = "#C4C9DA";
const SIDE_N = "#AFB5CB";

/** How far the floor runs past the block, in units. */
const uzribis = (b: Blokas) => Math.max(b.L[0], b.L[1], 1) * 0.12 + 0.35;

/**
 * The drawing, with its state handed in, so a test can render any moment of
 * it to a string. `Laipsnis` below and a lesson's hand in `pamokos/rankos.tsx`
 * both draw through this and through nothing else.
 */
export function LaipsnisView({
  focus,
  kartai,
  onPress,
  hand = false,
  spejimas,
}: {
  focus: LaipsnioFokusas;
  /** Presses per power, in the focus's order. */
  kartai: number[];
  /** Her hand on a block's press, where this frame hands it over. */
  onPress?: (i: number) => void;
  /** The pointing hand, under the press of this block. Which block is the
   *  caller's to say, because only the caller knows what the step or the
   *  question is waiting for; it points at a control and never at a count. */
  hand?: number | false;
  /** The label of her wrong answer, drawn beside the block where it has a
   *  picture. See `spejimasOf`. */
  spejimas?: string;
}) {
  const blokai = focus.laipsniai.map((l, i) => blokas(l.pagrindas, kartai[i] ?? 1));
  const klaida = spejimasOf(spejimas);
  const visi = klaida ? [...blokai, plokste(klaida.a, klaida.n)] : blokai;
  const cols = visi.length;
  const vbW = cols * COL_W;

  // One scale for every block on the screen, fitted to the widest and the
  // tallest of them, and one floor line under all of them. Two blocks drawn at
  // two scales could be made to say anything.
  const neigiamas = focus.laipsniai.some((l) => l.pagrindas < 0);
  const r = visi.map((b, i) => ribos(b, neigiamas && i < blokai.length));
  const top = Math.min(...r.map((b) => b.y0));
  const bottom = Math.max(...r.map((b) => b.y1));
  const s = Math.min(
    ...r.map((b) => (COL_W - 2 * PAD) / (b.x1 - b.x0)),
    (VB_H - 2 * PAD) / (bottom - top),
    // A single cube is drawn as big as the room allows, and no bigger than a
    // cube a finger would recognise as the thing she is about to copy.
    90,
  );
  const cy = PAD - top * s + (VB_H - 2 * PAD - (bottom - top) * s) / 2;
  const kameros = r.map((b, i) => ({ s, cy, cx: i * COL_W + COL_W / 2 - (s * (b.x0 + b.x1)) / 2 }));

  // The head and the press are drawn in the same coordinates as the block
  // they belong to. The lesson player squeezes a drawing to the height it has,
  // and a row of HTML under a squeezed SVG drifted a block's width off the
  // block it named. `vienetai.tsx` learned the same thing about a rail.
  const vbH = VB_H + GALVA + (onPress ? PRESS_H + HAND_ROOM : 0);

  return (
    <svg
      viewBox={`0 0 ${vbW} ${vbH}`}
      className="w-full h-auto"
      style={{ maxWidth: Math.min(vbW, 640) }}
      role="group"
      aria-label={focus.laipsniai.map((l, i) => zodziais(l.pagrindas, kartai[i] ?? 1)).join(", ")}
    >
      {visi.map((b, i) => (
        <g key={`${i}-${i < blokai.length ? (kartai[i] ?? 0) : "k"}`} className="priemone-arrives">
          <Piesinys b={b} k={kameros[i]} grindys={neigiamas && i < blokai.length} />
        </g>
      ))}
      {focus.laipsniai.map((l, i) => {
        const n = kartai[i] ?? 1;
        const pilna = n >= lubos(l.pagrindas);
        const x = i * COL_W + COL_W / 2;
        return (
          <g key={`galva-${i}`}>
            <Uzrasas pagrindas={l.pagrindas} n={n} x={x} y={VB_H} />
            {onPress && (
              <Spausk
                x={x}
                y={VB_H + GALVA}
                label={`· ${skliaustuose(l.pagrindas)}`}
                aria={
                  focus.laipsniai.length > 1
                    ? `dauginti ${i + 1} bloką iš ${rasyk(l.pagrindas)}`
                    : `dauginti iš ${rasyk(l.pagrindas)}`
                }
                pilna={pilna}
                hand={hand === i && !pilna}
                onPress={() => onPress(i)}
              />
            )}
          </g>
        );
      })}
      {klaida && (
        // Her answer, under its own drawing, in the words she pressed. The
        // label is the question's own text, so nothing here is a number the
        // drawing made up.
        <text
          x={blokai.length * COL_W + COL_W / 2}
          y={VB_H + 52}
          textAnchor="middle"
          fontFamily={FONT}
          fontSize={30}
          fontWeight={700}
          fill={FAINT}
        >
          {spejimas}
        </text>
      )}
    </svg>
  );
}

/** Under each block: the power, then the factors. */
const GALVA = 104;
/** The press, and under it the room the pointing hand needs to reach up from
 *  without being clipped at the knuckle. */
const PRESS_H = 52;
const HAND_ROOM = 52;
const PRESS_W = 104;

/** The press under one block. Drawn, so it cannot drift off the block. */
function Spausk({
  x,
  y,
  label,
  aria,
  pilna,
  hand,
  onPress,
}: {
  x: number;
  y: number;
  label: string;
  aria: string;
  pilna: boolean;
  hand: boolean;
  onPress: () => void;
}) {
  return (
    <g>
      <g
        role="button"
        aria-label={aria}
        aria-disabled={pilna || undefined}
        className={pilna ? undefined : "cursor-pointer"}
        opacity={pilna ? 0.3 : 1}
        onClick={pilna ? undefined : onPress}
      >
        <rect
          x={x - PRESS_W / 2}
          y={y}
          width={PRESS_W}
          height={PRESS_H - 4}
          rx={12}
          fill={PRESS_FILL}
          stroke={INK}
          strokeWidth={2.4}
        />
        <text
          x={x}
          y={y + 33}
          textAnchor="middle"
          fontFamily={FONT}
          fontSize={24}
          fontWeight={700}
          fill={INK}
        >
          {label}
        </text>
      </g>
      {hand && <Ranka x={x} y={y + PRESS_H - 8} />}
    </g>
  );
}

const PRESS_FILL = "#EEF8F7";

/**
 * The pointing hand, waiting below the press and reaching up onto it. Two
 * groups for the reason `tiese.tsx` gives: the bob is a CSS transform, and a
 * hand that places and animates itself on one node points at the corner.
 */
function Ranka({ x, y }: { x: number; y: number }) {
  return (
    <g transform={`translate(${x - 17} ${y + 4})`} aria-hidden="true" pointerEvents="none">
      <g className="priemone-point-up" style={{ transformBox: "fill-box" }}>
        <path
          d="M15 22 V8 a4.5 4.5 0 0 1 9 0 v12
             a3.8 3.8 0 0 1 7.6 0 v2
             a3.8 3.8 0 0 1 7.6 0 v10
             a16 16 0 0 1 -16 16 h-2.5
             a12 12 0 0 1 -10.3 -5.9 l-5.6 -9.4
             a4.2 4.2 0 0 1 7.1 -4.5 l3.1 4.3 z"
          fill="#FFFFFF"
          stroke={INK}
          strokeWidth={2.6}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      </g>
    </g>
  );
}

/** The block, its grid, and what she had before the last press. */
function Piesinys({ b, k, grindys }: { b: Blokas; k: Kamera; grindys: boolean }) {
  const [Lx, Ly, Lz] = b.L;
  const x0 = 1 - Lx;
  const y0 = 1 - Ly;
  const z0 = b.zemyn ? -Lz : 0;
  const z1 = b.zemyn ? 0 : Lz;
  const krastas = { stroke: INK, strokeWidth: 1.6, strokeLinejoin: "round" as const };

  const poly = (pts: [number, number, number][]) => pts.map(([x, y, z]) => pt(k, x, y, z)).join(" ");
  const linijos: { d: string; w: number; o: number }[] = [];
  const line = (a: [number, number, number], c: [number, number, number], w: number, o: number) =>
    linijos.push({ d: `M${pt(k, ...a)}L${pt(k, ...c)}`, w, o });

  // The copies she laid, heaviest at the biggest. Every line is a boundary
  // between two copies of something she built a press earlier, and the ones
  // too close to tell apart are left out rather than drawn as a grey smear.
  for (const { step, w, o } of zingsniai(b.a, b.E[0], k.s)) {
    for (let x = 1 - step; x > x0 + 1e-6; x -= step) {
      line([x, 1, z0], [x, 1, z1], w, o);
      line([x, y0, z1], [x, 1, z1], w, o);
    }
  }
  for (const { step, w, o } of zingsniai(b.a, b.E[1], k.s)) {
    for (let y = 1 - step; y > y0 + 1e-6; y -= step) {
      line([1, y, z0], [1, y, z1], w, o);
      line([x0, y, z1], [1, y, z1], w, o);
    }
  }
  for (const { step, w, o } of zingsniai(b.a, b.E[2], k.s)) {
    for (let i = 1; i * step < Lz - 1e-6; i += 1) {
      const z = b.zemyn ? -i * step : i * step;
      line([1, y0, z], [1, 1, z], w, o);
      line([x0, 1, z], [1, 1, z], w, o);
    }
  }

  const R = uzribis(b);
  const fx0 = Math.min(x0, 0) - R;
  const fy0 = Math.min(y0, 0) - R;
  // The floor, drawn only where a pagrindas is negative, because only there
  // does above or below mean anything. Under a block standing on it, and
  // **over** one hanging beneath it, translucent: seen from above, a block
  // under a floor that was painted first looks exactly like a block standing
  // on it, and the turn was the one thing the picture could not show.
  const grindu = grindys ? (
    <polygon
      points={poly([
        [1 + R, 1 + R, 0],
        [1 + R, fy0, 0],
        [fx0, fy0, 0],
        [fx0, 1 + R, 0],
      ])}
      fill={FLOOR}
      fillOpacity={b.zemyn ? 0.72 : 1}
      stroke={FAINT}
      strokeWidth={1.2}
    />
  ) : null;
  // A turned block shows its other side, so the parity of the turns is read
  // off the colour as well as off where it hangs.
  const [virsus, sonas, priekis] = b.zemyn ? [TOP_N, SIDE_N, FRONT_N] : [TOP, SIDE, FRONT];
  // **What she had, in brown, inside what she has.** After a press the brown
  // part is the block before it and the teal is the `a − 1` copies the press
  // laid beside it, so every screen reads „· 5 is five of what you had":
  // aⁿ = a · aⁿ⁻¹, drawn, which is the fact 2.3 presses on and 2.6 peels back.
  // It sits in the front corner, where the block has always grown from, and
  // its faces are the block's own front faces cut short.
  const P = b.P;
  const ruda =
    P === undefined
      ? null
      : (() => {
          const px0 = 1 - P[0];
          const py0 = 1 - P[1];
          const pz0 = b.zemyn ? -P[2] : 0;
          const pz1 = b.zemyn ? 0 : P[2];
          return (
            <g fill={UNIT} stroke={INK} strokeWidth={1.4} strokeLinejoin="round">
              <polygon points={poly([[1, py0, pz0], [1, 1, pz0], [1, 1, pz1], [1, py0, pz1]])} />
              <polygon points={poly([[px0, 1, pz0], [1, 1, pz0], [1, 1, pz1], [px0, 1, pz1]])} />
              {pz1 >= z1 - 1e-9 && (
                <polygon points={poly([[px0, py0, pz1], [1, py0, pz1], [1, 1, pz1], [px0, 1, pz1]])} />
              )}
            </g>
          );
        })();
  return (
    <g>
      {!b.zemyn && grindu}
      <polygon points={poly([[x0, y0, z1], [1, y0, z1], [1, 1, z1], [x0, 1, z1]])} fill={virsus} {...krastas} />
      <polygon points={poly([[1, y0, z0], [1, 1, z0], [1, 1, z1], [1, y0, z1]])} fill={sonas} {...krastas} />
      <polygon points={poly([[x0, 1, z0], [1, 1, z0], [1, 1, z1], [x0, 1, z1]])} fill={priekis} {...krastas} />
      {ruda}
      {linijos.map((l, i) => (
        <path key={i} d={l.d} stroke={INK} strokeWidth={l.w} opacity={l.o} fill="none" />
      ))}
      {b.zemyn && grindu}
    </g>
  );
}

/** The spacings a grid is drawn at along one edge that is `a^e` long. */
function zingsniai(a: number, e: number, s: number): { step: number; w: number; o: number }[] {
  const out: { step: number; w: number; o: number }[] = [];
  for (let j = e - 1; j >= 0; j -= 1) {
    const step = Math.pow(a, j);
    if (step * s < 5) break;
    // The biggest copies heaviest. On an edge one factor long the biggest
    // copies are single cubes, and those still have to read as cubes she can
    // count, so they get a line of their own weight rather than the faint one.
    const virsus = j === e - 1;
    const w = virsus ? (e >= 2 ? 1.6 : 1.1) : 0.7;
    const o = virsus ? (e >= 2 ? 0.9 : 0.7) : 0.45;
    out.push({ step, w, o });
  }
  return out;
}

/* ------------------------------------------------------------------ head */

/** A pagrindas as the book writes it inside a power: a negative one in
 *  brackets, because without them only the digits are raised. */
const skliaustuose = (a: number) => (a < 0 ? `(${rasyk(a)})` : rasyk(a));

/** The minus as a minus, never a hyphen. */
const rasyk = (a: number) => (a < 0 ? `−${-a}` : String(a));

/**
 * The power her presses made, and the factors it is made of.
 *
 * **The same instrument as `Trupmena` beside the bar, and for the same
 * reason.** It is never explained and it moves only when she moves the
 * drawing: it opens reading `5¹` over `5`, one press and it reads `5²` over
 * `5 · 5`. The small number is the count of factors in the row under it, and
 * it is the only place a rodiklis is written on this screen.
 */
function Uzrasas({ pagrindas, n, x, y }: { pagrindas: number; n: number; x: number; y: number }) {
  const a = skliaustuose(pagrindas);
  // The factors shrink to fit their column rather than wrap, since a row
  // broken over two lines reads as two numbers. Past the size a room can read
  // the middle of the row is elided: the rodiklis above it is the count, and
  // this row is there to say what is being counted.
  const pilna = Array.from({ length: n }, () => a).join(" · ");
  const telpa = (COL_W - 24) / (0.52 * pilna.length) >= 13;
  const eile = telpa ? pilna : `${a} · ${a} · … · ${a}`;
  const dydis = Math.min(19, (COL_W - 24) / (0.52 * eile.length));
  return (
    <g className="priemone-settles" aria-live="polite">
      <text x={x} y={y + 54} textAnchor="middle" fontFamily={FONT} fontWeight={700} fill={INK}>
        <tspan fontSize={50}>{a}</tspan>
        <tspan fontSize={28} dy={-24}>
          {n}
        </tspan>
      </text>
      <text x={x} y={y + 88} textAnchor="middle" fontFamily={FONT} fontSize={dydis} fill={FAINT}>
        {eile}
      </text>
    </g>
  );
}

/** What a screen reader hears for one block. */
function zodziais(pagrindas: number, n: number): string {
  return `${skliaustuose(pagrindas)} laipsniu ${n}`;
}

/* ------------------------------------------------------------------ hand */

/** The material behind „Noriu suprasti". Opens on the unit, every block, with
 *  nothing pressed. */
export function LaipsnisPriemone({
  focus,
  picked,
  onTouch,
  onDone,
}: {
  /** `unknown` for the reason `tiese.tsx` gives: this file cannot import the
   *  union it is a member of. */
  focus?: unknown;
  picked?: string;
  rung?: number;
  onTouch?: () => void;
  onDone?: () => void;
}) {
  const seed = asLaipsnis(focus) ?? NEUTRAL;
  const [kartai, setKartai] = useState<number[]>(() => seed.laipsniai.map(() => 1));
  const [moved, setMoved] = useState(false);

  // Worked once every block has reached the question's own rodiklis, or three
  // factors where the rodiklis is what is being asked. Three, because the rod
  // she opens on is the pagrindas itself and one press makes a flat, which a
  // row of rods laid side by side would also make; the second press is the
  // first one that copies a whole block. The options come back then and the
  // press stays live: this is the moment she may answer, never the moment she
  // must stop.
  const done = seed.laipsniai.every((l, i) => (kartai[i] ?? 1) >= (l.rodiklis ?? 3));
  // From an effect rather than from the updater, for the reason `lygtis.tsx`
  // gives: React may run an updater twice.
  useEffect(() => {
    if (done) onDone?.();
  }, [done, onDone]);

  return (
    <Priemone
      label="Laipsnis"
      action={
        <Reset
          onClick={() => {
            setKartai(seed.laipsniai.map(() => 1));
            setMoved(false);
          }}
        />
      }
    >
      <LaipsnisView
        focus={seed}
        kartai={kartai}
        // Under the first block short of where the question needs it, and
        // gone once her hand has moved anything.
        hand={moved ? false : Math.max(0, seed.laipsniai.findIndex((l, i) => (kartai[i] ?? 1) < (l.rodiklis ?? 3)))}
        spejimas={picked}
        onPress={(i) => {
          onTouch?.();
          setMoved(true);
          setKartai((was) =>
            was.map((n, k) => (k === i ? Math.min(n + 1, lubos(seed.laipsniai[k].pagrindas)) : n)),
          );
        }}
      />
    </Priemone>
  );
}

/** The block no question asks about. */
const NEUTRAL: LaipsnioFokusas = { kind: "laipsnis", laipsniai: [{ pagrindas: 3 }] };
